import { TableStaticActions, useModal, withModalConditionalRender } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'

import { useRetryBillIntegrationSyncMutation } from '@features/attachments/hooks/useRetryBillSync'
import { VoidBillConfirmationModal as VoidBillConfirmationModalComponent } from '@features/bills/elements/VoidBillConfirmationModal'
import { VoidBillPaymentConfirmationModal as VoidBillPaymentConfirmationModalComponent } from '@features/bills/elements/VoidBillPaymentConfirmationModal'
import { useRegisterBillPayment } from '@features/bills/hooks/useRegisterBillPayment'
import { Bill } from '@features/bills/types/bill'
import { getLatestIntegrationStatus } from '@views/bills/utils/getLatestIntegrationStatus'

import { useSegment } from '../../../../../../../../../hooks'
import { useDeleteBillAction } from '../../../../../../../elements/BillEditableForm/hooks/useDeleteBillAction'
import { BillReconciliationModal as BillReconciliationModalComponent } from '../../../../../../../elements/BillReconciliationModal'
import { DeleteBillConfirmationModal as DeleteBillConfirmationModalComponent } from '../../../../../../../elements/DeleteBillConfirmationModal'
import { BillsTableStaticActionType } from '../../../../../../../enums/billsTableStaticActionType'
import { BillsTableStaticActionItem } from '../../../../../../../types/billsTableStaticActionItem'

const DeleteBillConfirmationModal = withModalConditionalRender(DeleteBillConfirmationModalComponent)
const VoidBillConfirmationModal = withModalConditionalRender(VoidBillConfirmationModalComponent)
const VoidBillPaymentConfirmationModal = withModalConditionalRender(VoidBillPaymentConfirmationModalComponent)
const BillReconciliationModal = withModalConditionalRender(BillReconciliationModalComponent)

interface BillsTableStaticActionsProps {
  bill: Bill
  billId: string
  items: BillsTableStaticActionItem[]
  paymentId?: string
}

export const getDeleteBillModalId = (id: string) => `${id}-delete-bill-confirmation`
export const getVoidBillModalId = (id: string) => `${id}-void-bill-confirmation`
export const getVoidBillPaymentModalId = (id: string) => `${id}-void-bill-payment-confirmation`
export const getBillReconciliationModalId = (id: string) => `${id}-bill-reconciliation-static-action`

export const BillsTableStaticActions = ({
  bill,
  billId,
  items,
  paymentId,
}: BillsTableStaticActionsProps): ReactElement => {
  const { track } = useSegment()

  const voidBillModalId = getVoidBillModalId(billId)
  const deleteBillModalId = getDeleteBillModalId(billId)
  const voidBillPaymentModalId = getVoidBillPaymentModalId(billId)
  const billReconciliationModalId = getBillReconciliationModalId(billId)
  const { open: openVoidConfirmationModal } = useModal(voidBillModalId)
  const { open: openVoidPaymentConfirmationModal } = useModal(voidBillPaymentModalId)
  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModal(deleteBillModalId)
  const { open: openBillReconciliationModal } = useModal(billReconciliationModalId)

  const latestIntegrationStatus = getLatestIntegrationStatus(bill.integrationStatus)
  const { retryIntegration } = useRetryBillIntegrationSyncMutation({ integrationStatus: latestIntegrationStatus })

  const { handleRegisterPayment } = useRegisterBillPayment({
    bill,
    openReconciliationModal: openBillReconciliationModal,
  })

  const { deleteBill, isDeleting } = useDeleteBillAction({
    onSuccess: async () => {
      closeDeleteConfirmationModal()
    },
  })

  const handleDeleteBill = useCallback(() => {
    if (!billId) {
      return
    }

    deleteBill(billId)
  }, [billId, deleteBill])

  const handleStaticActionSelect = useCallback(
    (id: string, value: BillsTableStaticActionType) => {
      switch (value) {
        case BillsTableStaticActionType.Credit: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.Credit,
          })
          break
        }
        case BillsTableStaticActionType.Delete: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.Delete,
          })
          openDeleteConfirmationModal()
          break
        }
        case BillsTableStaticActionType.Duplicate: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.Duplicate,
          })
          break
        }
        case BillsTableStaticActionType.Edit: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.Edit,
          })
          break
        }
        case BillsTableStaticActionType.RegisterPayment: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.RegisterPayment,
          })
          handleRegisterPayment()
          break
        }
        case BillsTableStaticActionType.Void: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.Void,
          })
          openVoidConfirmationModal()
          break
        }
        case BillsTableStaticActionType.VoidPayment: {
          track('xxx - bookkeeping - QuickAction Clicked', {
            list: 'Bills',
            button: BillsTableStaticActionType.VoidPayment,
          })
          openVoidPaymentConfirmationModal()
          break
        }
        case BillsTableStaticActionType.RetrySync: {
          // tracking is included in the hook
          retryIntegration()
          break
        }
      }
    },
    [
      track,
      retryIntegration,
      openDeleteConfirmationModal,
      handleRegisterPayment,
      openVoidConfirmationModal,
      openVoidPaymentConfirmationModal,
    ],
  )

  return (
    <>
      <TableStaticActions items={items} onSelect={handleStaticActionSelect} />

      <VoidBillConfirmationModal id={voidBillModalId} billId={billId} />
      <DeleteBillConfirmationModal id={deleteBillModalId} onOk={handleDeleteBill} isDeleting={isDeleting} />
      {bill && <BillReconciliationModal subject={bill} id={billReconciliationModalId} />}
      {paymentId && <VoidBillPaymentConfirmationModal id={voidBillPaymentModalId} paymentId={paymentId} />}
    </>
  )
}
